/**
 * Copyright 2022 (C) Blink - All Rights Reserved
 *
 * This source code is protected under international copyright law.  All rights
 * reserved and protected by the copyright holders.
 * All files are confidential and only available to authorized individuals with the
 * permission of the copyright holders. If you encounter any file and do not have
 * permission, please get in touch with the copyright holders and delete this file.
 */
import { zodResolver } from "@hookform/resolvers/zod";
import { useAppAssetsWithSrc } from "Theme/EVENTS_ASSETS";
import HidePasswordIcon from "assets/Icons/HidePassword";
import ShowPasswordIcon from "assets/Icons/ShowPassword";
import {
  Button,
  FontAwesomeIcon,
  Input,
  PasswordRulesChecklist,
  Stack,
  Text,
  isPasswordRulesMet,
} from "components";
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
  FormMessage,
} from "components/FormV2/FormV2";
import LOCAL_STORAGE_KEYS, {
  SESSION_STORAGE_KEYS,
} from "constants/STORAGE_KEYS";
import useEventRouting from "hooks/useEventRouting";
import { createNewPassword } from "lib/loginApi";
import { useEffect, useMemo, useState } from "react";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import ROUTES from "routes";
import { useUI } from "state";
import { useAuth } from "state/AuthContext";
import { useGlobal } from "state/GeneralContext";
import { useUser } from "state/UserContext";
import { ScreenPage } from "templates";
import sha256 from "utils/encode";
import { pushToDataLayer } from "utils/gtm";
import openToast from "utils/openToast";
import useLocalStorage from "utils/useLocalStorage";
import { z } from "zod";

const clearStepStorage = () => {
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.stepToken);
  sessionStorage.removeItem(SESSION_STORAGE_KEYS.tempPasswordHash);
};

const CreateNewPasswordPage = () => {
  const { t } = useTranslation();
  const { navigateWithEvent } = useEventRouting();
  const { colorScheme } = useUI();
  const EVENT_ASSETS = useAppAssetsWithSrc();
  const { handleLogin } = useAuth();
  const { setEvents } = useGlobal();
  const { setUserData } = useUser();
  const [, setToken] = useLocalStorage<string | null>(
    LOCAL_STORAGE_KEYS.token,
    null,
  );
  const [, setId] = useLocalStorage<string | null>(LOCAL_STORAGE_KEYS.id, null);
  const [, setName] = useLocalStorage<string | null>(
    LOCAL_STORAGE_KEYS.name,
    null,
  );
  const [, setIsActive] = useLocalStorage<boolean | null>(
    LOCAL_STORAGE_KEYS.isActive,
    null,
  );
  const [, setEventCode] = useLocalStorage<string | null>(
    LOCAL_STORAGE_KEYS.eventCode,
    null,
  );
  const [, setRegisterPageStage] = useLocalStorage<string | null>(
    LOCAL_STORAGE_KEYS.registerPageStage,
    null,
  );

  // Contract M1: this screen is authorized by the short-lived step token the
  // /login (or /magic-link) 201 step response issued. sessionStorage keeps it
  // across reloads (resume on this screen) but scopes it to the tab.
  const stepToken = sessionStorage.getItem(SESSION_STORAGE_KEYS.stepToken);

  // No step token → nothing to authorize the reset; back to login, which
  // re-issues a step response for accounts that still require the change.
  useEffect(() => {
    if (!stepToken) {
      navigateWithEvent(ROUTES.login, { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stepToken]);

  const [isNewPasswordShown, setIsNewPasswordShown] = useState(false);
  const [isRepeatPasswordShown, setIsRepeatPasswordShown] = useState(false);

  const schema = useMemo(
    () =>
      z
        .object({
          // The five strength rules gate the submit button via the live
          // checklist (isPasswordRulesMet) instead of an inline error here.
          newPassword: z.string(),
          repeatPassword: z.string(),
        })
        .refine((data) => data.newPassword === data.repeatPassword, {
          message: t("forms.validation.passwordMismatch"),
          path: ["repeatPassword"],
        }),
    [t],
  );

  const form = useForm<z.infer<typeof schema>>({
    resolver: zodResolver(schema),
    mode: "onChange",
    defaultValues: { newPassword: "", repeatPassword: "" },
  });

  const {
    handleSubmit,
    watch,
    setError,
    formState: { isSubmitting },
  } = form;

  const newPassword = watch("newPassword");
  const repeatPassword = watch("repeatPassword");
  const canSubmit =
    isPasswordRulesMet(newPassword) &&
    repeatPassword.length > 0 &&
    newPassword === repeatPassword;

  const onSubmit = async (values: z.infer<typeof schema>) => {
    const hashedPassword = await sha256(values.newPassword);

    // Client-side backstop (contract: same_as_temporary): the hash of the
    // temporary password was stashed at login. The magic-link path can't know
    // it, so the server remains the authority — this only saves a round trip.
    const tempHash = sessionStorage.getItem(
      SESSION_STORAGE_KEYS.tempPasswordHash,
    );
    if (tempHash && hashedPassword === tempHash) {
      setError("newPassword", {
        type: "validate",
        message: t("auth.createNewPassword.sameAsTemporaryError"),
      });
      return;
    }

    try {
      const api = await createNewPassword(stepToken, hashedPassword);
      const { data } = api;

      // 200 is a full login payload — swap the step token for the session and
      // proceed without another login (mirrors LoginPage's success handling).
      // Step state is dropped only inside the handled branches: a null/204
      // payload throws below and must NOT consume the flow.
      if (data.is_active === false) {
        clearStepStorage();
        setToken(data.token);
        setIsActive(false);
        setEventCode(data.event_token);
        setRegisterPageStage(data.token ? "verify" : "signup");
        return navigateWithEvent(ROUTES.registerLink.verifyLink);
      }

      clearStepStorage();
      setToken(data.token);
      setId(data.hash_id);
      setName(data.first_name);
      setIsActive(data.is_active);
      setEvents(data.user_events);
      setUserData((prev: any) => ({
        ...prev,
        first_name: data.first_name,
        last_name: data.last_name,
        hash_id: data.hash_id,
      }));
      handleLogin();
      pushToDataLayer({
        event: "login",
        screen_name: "create_new_password",
        journey_step: "create_new_password",
        action: "password_reset",
      });
      openToast(t("auth.createNewPassword.successMessage"), "success");
      // LoginPage's logged-in machinery routes onward (event switcher or the
      // eventId effect → dashboard). replace: Back must not re-open this form.
      navigateWithEvent(ROUTES.login, { replace: true });
    } catch (error) {
      // FetchError is promise-like: it resolves to the processed error, or to
      // undefined for network failures / statuses it does not handle.
      const resolved = (await Promise.resolve(error).catch(() => undefined)) as
        | { error?: string; statusCode?: number }
        | undefined;

      // 401 = step token expired (30 min) or already used. FetchError's
      // is_expired handling has scheduled the redirect to login (toasts are
      // suppressed via alertError:false to avoid doubling the 422 inline
      // errors) — surface the message and drop the dead step token so
      // nothing forces the user back here.
      if (resolved?.statusCode === 401) {
        clearStepStorage();
        openToast(
          resolved?.error || t("common.messages.sessionExpired"),
          "error",
        );
        return;
      }

      // 422 backstops: invalid_format / mismatch / same_as_temporary.
      setError("newPassword", {
        type: "server",
        message: resolved?.error || t("auth.resetPassword.resetError"),
      });
    }
  };

  // The redirect effect handles the missing-token case; render nothing in
  // the meantime so the form never flashes before navigation.
  if (!stepToken) return null;

  const eyeToggle = (isShown: boolean, toggle: () => void) => (
    <div
      className="absolute end-2 inset-y-0 my-auto flex items-center bg-secondary-accent h-fit p-1 rounded-full cursor-pointer"
      onClick={toggle}
    >
      {isShown ? <HidePasswordIcon /> : <ShowPasswordIcon />}
    </div>
  );

  return (
    <ScreenPage
      desktopBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.src
          : EVENT_ASSETS.login_BG_desktopDark.src
      }
      desktopBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_desktop.fallbackSrc
          : EVENT_ASSETS.login_BG_desktopDark.fallbackSrc
      }
      mobileBG={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.src
          : EVENT_ASSETS.login_BG_mobileDark.src
      }
      mobileBGFallback={
        colorScheme === "light"
          ? EVENT_ASSETS.login_BG_mobile.fallbackSrc
          : EVENT_ASSETS.login_BG_mobileDark.fallbackSrc
      }
    >
      <h2 className="font-bold mb-4 text-2xl md:text-4xl -text--theme-colors-text-title">
        {t("auth.createNewPassword.title")}
      </h2>
      <p className="-text--theme-colors-text-body">
        {t("auth.createNewPassword.subtitle")}
      </p>
      <Form {...form}>
        <Stack
          component="form"
          className="mt-10"
          onSubmit={handleSubmit(onSubmit)}
        >
          <FormField
            name="newPassword"
            // Legacy Input is not forwardRef-compatible — drop RHF's ref.
            render={({ field: { ref: _ref, ...field } }) => (
              <FormItem>
                <FormLabel isRequired>
                  {t("auth.createNewPassword.newPasswordLabel")}
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      type={isNewPasswordShown ? "text" : "password"}
                      placeholder={t(
                        "auth.createNewPassword.newPasswordPlaceholder",
                      )}
                    />
                  </FormControl>
                  {eyeToggle(isNewPasswordShown, () =>
                    setIsNewPasswordShown((prev) => !prev),
                  )}
                </div>
                <FormMessage />
              </FormItem>
            )}
          />
          <PasswordRulesChecklist password={newPassword} className="mt-4" />
          <FormField
            name="repeatPassword"
            render={({ field: { ref: _ref, ...field } }) => (
              <FormItem className="mt-6">
                <FormLabel isRequired>
                  {t("auth.createNewPassword.repeatPasswordLabel")}
                </FormLabel>
                <div className="relative">
                  <FormControl>
                    <Input
                      {...field}
                      type={isRepeatPasswordShown ? "text" : "password"}
                      placeholder={t(
                        "auth.createNewPassword.repeatPasswordPlaceholder",
                      )}
                    />
                  </FormControl>
                  {eyeToggle(isRepeatPasswordShown, () =>
                    setIsRepeatPasswordShown((prev) => !prev),
                  )}
                </div>
                <FormMessage className="min-h-4" />
              </FormItem>
            )}
          />
          <Stack className="mt-8">
            <Button
              type="submit"
              variant="primary"
              size="lg"
              disabled={!canSubmit || isSubmitting}
              isLoading={isSubmitting}
              onClick={handleSubmit(onSubmit)}
            >
              <div className="flex justify-center items-center gap-x-s10">
                <Text>{t("auth.createNewPassword.submitButton")}</Text>
                <FontAwesomeIcon name="fa-chevron-right" />
              </div>
            </Button>
          </Stack>
        </Stack>
      </Form>
    </ScreenPage>
  );
};

export default CreateNewPasswordPage;
